import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as PieTooltip } from 'recharts';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoney, fmtMoneyExact, fmtHours, fmtNumber } from './format';
import { card, chart, tbl } from '../../theme';

// Slice colors come from the shared co-visible set, which is validated
// all-pairs — the correct test for a donut, where every slice is comparable
// with every other rather than just its neighbours.
//
// The previous eight ad-hoc hues failed that test badly: violet and blue
// were ΔE 1.3 apart under deuteranopia (indistinguishable), and the two
// oranges were ΔE 9.6 apart even with full colour vision. They were also
// assigned with `i % length`, so a ninth manager reused the first colour.
//
// Job profiles and managers are unbounded lists, so the tail folds into a
// single neutral "Other" slice rather than growing new hues.
const MAX_SLICES = chart.coVisible.length;
const OTHER_LABEL = 'Other';

function toSlices(rows, labelKey, valueKey) {
  const all = rows
    .map((r) => ({ name: r[labelKey] || '—', value: Number(r[valueKey] || 0) }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  if (all.length <= MAX_SLICES) {
    return all.map((d, i) => ({ ...d, color: chart.coVisible[i] }));
  }
  const head = all.slice(0, MAX_SLICES - 1)
    .map((d, i) => ({ ...d, color: chart.coVisible[i] }));
  const tail = all.slice(MAX_SLICES - 1);
  return [
    ...head,
    {
      name:  `${OTHER_LABEL} (${tail.length})`,
      value: tail.reduce((sum, d) => sum + d.value, 0),
      color: chart.other,
    },
  ];
}

// The tables beside each donut list every row in their own order, while the
// donut sorts by value and folds a tail. Looking the dot colour up by name
// keeps the two in agreement — a row inside the folded tail gets the same
// neutral as the "Other" slice it was merged into.
function sliceColorLookup(rows, labelKey, valueKey = 'ot_amount') {
  const byName = new Map(toSlices(rows, labelKey, valueKey).map((d) => [d.name, d.color]));
  return (name) => byName.get(name || '—') ?? chart.other;
}

function MiniDonut({ rows, labelKey, valueKey = 'ot_amount' }) {
  const data  = toSlices(rows, labelKey, valueKey);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return null;
  return (
    <div style={s.donutWrap}>
      <div style={{ position: 'relative', width: 200, height: 200 }}>
        <PieChart width={200} height={200}>
          <Pie
            data={data}
            cx={100} cy={100}
            innerRadius={62} outerRadius={88}
            dataKey="value"
            paddingAngle={data.length > 1 ? 2 : 0}
            startAngle={90} endAngle={-270}
            stroke="none"
          >
            {data.map((d) => (
              <Cell key={d.name} fill={d.color} />
            ))}
          </Pie>
          <PieTooltip
            formatter={(v, _n, p) => [fmtMoney(v), p.payload.name]}
            contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #E5E7EB', padding: '4px 8px' }}
          />
        </PieChart>
        <div style={s.donutCenter}>
          <div style={s.donutVal}>{fmtMoney(total)}</div>
          <div style={s.donutLbl}>OT cost</div>
        </div>
      </div>
    </div>
  );
}

// Level 2 — cost-center drill-down: OT-type breakdown (all roles) +
// employee list (person-level; hidden for read_only — canViewPeople=false).
export default function CostCenterDetail({ ccId, ccName, periodId, scopeParams = '', filterQ = '', canViewPeople = true, onSelectEmployee, embedded = false }) {
  // One pay run when the reader picked a bar off the trend, otherwise the span
  // the board itself is on. Before the span existed this took a period_id the
  // page could not supply on a month or year-to-date view, and fell back to the
  // newest run in the dataset -- so these figures answered a question nobody on
  // screen had asked.
  const scopeQ = periodId ? `period_id=${periodId}` : scopeParams;
  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-costcenter', ccId, scopeQ, filterQ],
    queryFn:  () => api.get(`/overtime/costcenter/${encodeURIComponent(ccId)}?${scopeQ}${filterQ}`),
    enabled:  !!ccId && !!scopeQ,
  });

  if (isLoading) return <div style={embedded ? s.panelEmbedded : s.panel}><LoadingSpinner /></div>;
  if (error)     return <div style={embedded ? s.panelEmbedded : s.panel}><div style={s.err}>{error.message}</div></div>;

  const otTypes       = data?.ot_types      || [];
  const byJobProfile  = data?.by_job_profile || [];
  const byManager     = data?.by_manager     || [];
  const employees     = data?.employees     || [];
  const driverMetrics = data?.driver_metrics || null;
  // Trust the server's flag as the authority, falling back to the prop.
  const showPeople = data?.can_view_employees !== false && canViewPeople;

  // Shared between each donut and the table beside it, so a row's dot always
  // matches the slice it contributes to.
  const jobProfileColor = sliceColorLookup(byJobProfile, 'job_profile');
  const managerColor    = sliceColorLookup(byManager, 'manager');

  return (
    <div style={embedded ? s.panelEmbedded : s.panel}>
      <div style={s.headRow}>
        <div style={s.title}>{ccName || data?.cost_center?.name} — Overtime Detail</div>
        {/* Which payroll this is. A table of employee totals with no span named
            beside it is the thing that got reconciled against the wrong month. */}
        {data?.scope?.label && (
          <div style={s.scopeNote}>
            {data.scope.label}
            {data.scope.period_count > 1
              ? ` · ${data.scope.period_count} pay runs`
              : ''}
          </div>
        )}
      </div>

      {driverMetrics && (
        <div style={s.driverStrip}>
          <span style={s.driverLabel}>Driver OT Metrics ({driverMetrics.driver_count} driver{driverMetrics.driver_count !== 1 ? 's' : ''})</span>
          <div style={s.driverKpis}>
            <div style={s.driverKpi}>
              <div style={s.driverKpiVal}>{fmtHours(driverMetrics.ot_hours_per_driver)}</div>
              <div style={s.driverKpiLbl}>OT Hrs / Driver</div>
            </div>
            <div style={s.driverKpi}>
              <div style={s.driverKpiVal}>{fmtMoneyExact(driverMetrics.ot_amount_per_driver)}</div>
              <div style={s.driverKpiLbl}>OT $ / Driver</div>
            </div>
          </div>
        </div>
      )}

      <div style={{ ...s.cols, gridTemplateColumns: showPeople ? '1fr 1.3fr' : '1fr' }}>
        {/* OT type breakdown */}
        <div style={s.col}>
          <div style={s.colTitle}>By Overtime Type</div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>OT Type</th>
                <th style={s.thR}>Hours</th>
                <th style={s.thR}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {otTypes.map((t) => (
                <tr key={t.ot_type} style={s.tr}>
                  <td style={s.td}>{t.ot_type}</td>
                  <td style={s.tdR}>{fmtHours(t.hours)}</td>
                  <td style={s.tdR}>{fmtMoney(t.amount)}</td>
                </tr>
              ))}
              {!otTypes.length && <tr><td colSpan={3} style={s.empty}>No data.</td></tr>}
            </tbody>
          </table>

          {byJobProfile.length > 0 && (
            <>
              <div style={{ ...s.colTitle, marginTop: '14px' }}>By Job Profile</div>
              <div>
                <MiniDonut rows={byJobProfile} labelKey="job_profile" />
                <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Job Profile</th>
                        <th style={s.thR}>Empl</th>
                        <th style={s.thR}>OT Hrs</th>
                        <th style={s.thR}>OT Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byJobProfile.map((p) => (
                        <tr key={p.job_profile} style={s.tr}>
                          <td style={s.td}>
                            <span style={{ ...s.dot, background: jobProfileColor(p.job_profile) }} />
                            {p.job_profile}
                          </td>
                          <td style={s.tdR}>{fmtNumber(p.employee_count)}</td>
                          <td style={s.tdR}>{fmtHours(p.ot_hours)}</td>
                          <td style={s.tdR}>{fmtMoney(p.ot_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                </table>
              </div>
            </>
          )}

          {byManager.length > 0 && (
            <>
              <div style={{ ...s.colTitle, marginTop: '14px' }}>By Manager</div>
              <div>
                <MiniDonut rows={byManager} labelKey="manager" />
                <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Manager</th>
                        <th style={s.thR}>Empl</th>
                        <th style={s.thR}>OT Hrs</th>
                        <th style={s.thR}>OT Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byManager.map((m) => (
                        <tr key={m.manager} style={s.tr}>
                          <td style={s.td}>
                            <span style={{ ...s.dot, background: managerColor(m.manager) }} />
                            {m.manager}
                          </td>
                          <td style={s.tdR}>{fmtNumber(m.employee_count)}</td>
                          <td style={s.tdR}>{fmtHours(m.ot_hours)}</td>
                          <td style={s.tdR}>{fmtMoney(m.ot_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Employee list — person-level; only for non-read_only roles */}
        {showPeople && (
        <div style={s.col}>
          <div style={s.colTitle}>Employees ({employees.length})</div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Employee</th>
                <th style={s.thR}>OT Hours</th>
                <th style={s.thR}>OT Amount</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.employee_number} style={s.trClick}
                    onClick={() => onSelectEmployee?.(e.employee_number)}
                    title="View line-item detail">
                  <td style={s.td}>
                    <div style={s.empName}>{e.employee_name || e.employee_number}</div>
                    <div style={s.empSub}>#{e.employee_number}{e.job_profile ? ` · ${e.job_profile}` : ''}</div>
                  </td>
                  <td style={s.tdR}>{fmtHours(e.ot_hours)}</td>
                  <td style={s.tdR}>{fmtMoneyExact(e.ot_amount)}</td>
                  <td style={s.tdChev}><ChevronRight size={14} color="#9CA3AF" /></td>
                </tr>
              ))}
              {!employees.length && <tr><td colSpan={4} style={s.empty}>No employees.</td></tr>}
            </tbody>
          </table>
          <div style={s.hint}>{fmtNumber(employees.length)} employee(s) — click a row for line-item detail.</div>
        </div>
        )}
      </div>

    </div>
  );
}

const s = {
  panel:    { ...card.sunk, borderRadius: '0 0 10px 10px', borderTop: 'none', padding: '18px' },
  panelEmbedded: { background: '#F9FAFB', padding: '14px 18px' },
  scopeNote: { fontSize: '11px', color: '#6B7280', fontWeight: 500, marginTop: '2px' },
  headRow:  { marginBottom: '14px' },
  title:    { fontSize: '14px', fontWeight: 700, color: '#1B2A4A' },
  cols:     { display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: '20px' },
  col:      { minWidth: 0 },
  colTitle: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', marginBottom: '8px' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' },
  th:       { ...tbl.th, ...tbl.th },
  thR:      { ...tbl.thR, ...tbl.thR },
  tr:       { ...tbl.tr, ...tbl.tr },
  trClick:  { ...tbl.trHover },
  td:       { ...tbl.td, ...tbl.td },
  tdR:      { ...tbl.tdR, ...tbl.tdR },
  tdChev:   { padding: '8px 8px', textAlign: 'right', width: '28px' },
  empName:  { fontWeight: 500, color: '#111827' },
  empSub:   { fontSize: '11px', color: '#9CA3AF', marginTop: '1px' },
  empty:    { textAlign: 'center', padding: '18px', color: '#9CA3AF' },
  err:      { color: '#DC2626', fontSize: '13px', padding: '8px' },
  hint:     { fontSize: '11px', color: '#9CA3AF', marginTop: '8px' },
  driverStrip: { background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' },
  driverLabel: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#1D4ED8', flexShrink: 0 },
  driverKpis:  { display: 'flex', gap: '20px' },
  driverKpi:   { textAlign: 'center' },
  driverKpiVal:{ fontSize: '15px', fontWeight: 700, color: '#1E3A5F', fontVariantNumeric: 'tabular-nums' },
  driverKpiLbl:{ fontSize: '10px', color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '2px' },
  donutWrap:   { display: 'flex', justifyContent: 'center', marginBottom: '10px' },
  donutCenter: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' },
  donutVal:    { fontSize: '13px', fontWeight: 700, color: '#1B2A4A', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 },
  donutLbl:    { fontSize: '10px', color: '#9CA3AF', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.3px' },
  dot:         { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, flexShrink: 0, verticalAlign: 'middle' },
};
